import React, { Component } from 'react';
import PropTypes from 'prop-types';
import identity from 'lodash/identity';
import CSSTransition from 'react-transition-group/CSSTransition';
import styled from 'styled-components';
import { connect } from 'react-redux';
import cond from 'lodash/cond';

import {
    SuiContainer,
    SuiTextInput,
    SuiButton,
    SuiTooltip,
    SuiTooltipPopover,
    SuiRadio,
    SuiCheckbox,
    SuiCollapsible,
} from '@splashthat/sui';

import DotDragGripIcon from 'common/icons/DotDragGrip';
import TrashIcon from 'common/icons/Trash';

import { TRANSITION_TIMEOUT } from 'common/constants';
import { containerPaddingXs } from 'common/styles/variables';

import { isFormTypeTicketSale } from 'common/entities/form/selectors';
import { isAllowedLogicNode, getEditFormNode, getResponseLimitFlag, getFormId } from '../../selectors';
import { DISABLED_OTHER } from './FormEditorEditQuestionOptions';

const DEFAULT_BUTTON_TIP = 'Selected by default';
const REMOVE_BUTTON_TITLE = 'Delete';
const OPTION_LABEL_LABEL_SIMPLE = 'Option';
const OPTION_LABEL_PLACEHOLDER_SIMPLE = 'Enter Option';
const OPTION_LABEL_LABEL = 'Option Label';
const OPTION_LABEL_PLACEHOLDER = 'Enter Label';
const OPTION_TOOLTIP_LABEL =
    'Add a numerical value here. This will determine the maximum number of guests that can choose this option, and this option will close when it is hit. Guests who RSVP No will not count towards this limit.';
const OPTION_TOOLTIP_LABEL_FREEMIUM = `${OPTION_TOOLTIP_LABEL}. This feature is not included in your plan. Please request a demo if you would like more information`;
const OPTION_VALUE_LABEL = 'Option Value';
const OPTION_VALUE_PLACEHOLDER = 'Enter Value';
const DONE_BUTTON_LABEL = 'Done';
const RESPONSE_LIMIT_LABEL = 'Response Limit';
const RESPONSE_LIMIT_PLACEHOLDER = 'Enter Limit';
const RESPONSE_LIMIT_HEADER_TEXT = 'Limit:';

const CLEAN_BOX = 'clean-box';
const NO_PADDED = 'no-padded';

const TrimmedLabel = styled.p`
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ResponseLimitContainer = styled.div`
    margin-right: ${containerPaddingXs};
`;

const BASE_CLASS_NAME = 'FormEditorEditQuestionOption';

class FormEditorEditQuestionOption extends Component {
    static propTypes = {
        optionId: PropTypes.string.isRequired,
        optionLabel: PropTypes.string,
        optionValue: PropTypes.string,
        responseLimit: PropTypes.number,
        allowMultipleSelected: PropTypes.bool,
        optionSelected: PropTypes.bool,
        responseLimitFlag: PropTypes.bool.isRequired,
        removeOption: PropTypes.func.isRequired,
        updateOption: PropTypes.func.isRequired,
        setOptionSelected: PropTypes.func.isRequired,
        updateOptionResponseLimit: PropTypes.func.isRequired,
        onCollapsibleOpened: PropTypes.func.isRequired,
        isOpen: PropTypes.bool.isRequired,
        showEditValueInputs: PropTypes.bool.isRequired,
        connectDragSource: PropTypes.func,
        isDropTargetActive: PropTypes.bool,
        dropTargetYPosition: PropTypes.number,
        isAllowedLogicNode: PropTypes.bool.isRequired,
        isAllowedResponseLimit: PropTypes.bool.isRequired,
        /** disabled is passed when question editing is locked */
        disabled: PropTypes.bool.isRequired,
        protectedField: PropTypes.bool,
    };

    static defaultProps = {
        optionLabel: '',
        optionValue: '',
        responseLimit: null,
        allowMultipleSelected: false,
        optionSelected: false,
        connectDragSource: identity,
        isDropTargetActive: false,
        dropTargetYPosition: null,
        protectedField: false,
    };

    state = {
        isHovered: false,
    };

    removeOption() {
        const { optionId, removeOption } = this.props;
        removeOption(optionId);
    }

    handleEditLabel = (event, label) => {
        const { optionId, updateOption, optionValue, optionLabel } = this.props;

        // If previous value was same as label, keep them in sync.
        const value = optionValue === optionLabel ? label : optionValue;

        updateOption(optionId, { label, value });
    };

    handleTrimLabel = (event) => this.handleEditLabel(event, this.props.optionLabel.trim());

    handleEditValue = (event, value) => {
        const { optionId, updateOption } = this.props;
        updateOption(optionId, { value });
    };

    handleClickSelected = (event, selected = true) => {
        event.stopPropagation();
        const { optionId, optionSelected, setOptionSelected } = this.props;
        setOptionSelected(optionId, !optionSelected);
    };

    handleCollapsibleToggleHeader = (isOpen) => {
        const { optionId, onCollapsibleOpened } = this.props;
        onCollapsibleOpened(optionId);

        if (!isOpen) {
            this.setState({ isHovered: false });
        }
    };

    handleClickRemove = (event) => {
        this.removeOption();
        event.stopPropagation();
    };

    handleMouseOver = () => {
        this.setState({ isHovered: true });
    };

    handleMouseLeave = () => {
        this.setState({ isHovered: false });
    };

    handleEditResponseLimit = (event) => {
        let { value } = event.target;
        const { optionId, updateOptionResponseLimit } = this.props;

        value = parseInt(value, 10);

        if (Number.isNaN(value)) {
            value = null;
        }

        if (value !== null) {
            value = Math.max(0, value);
        }
        updateOptionResponseLimit(optionId, value);
    };

    renderHeader() {
        const {
            optionLabel,
            optionValue,
            allowMultipleSelected,
            optionSelected,
            responseLimit,
            isOpen,
            connectDragSource,
        } = this.props;
        const { isHovered } = this.state;

        // making sure delete button is actionable from touch screens without hover states.
        const isDeleteButtonVisible = isOpen || isHovered;

        return (
            <SuiContainer variation="flex-horizontal flex-inherit" className={`${BASE_CLASS_NAME}__header`}>
                <SuiContainer variation="flex-group flex" className={`${BASE_CLASS_NAME}__icon-group`}>
                    {connectDragSource(
                        <div className={`${BASE_CLASS_NAME}__drag-handle`}>
                            <SuiButton
                                draggable="true"
                                variation="navigation text"
                                size="small"
                                icon={DotDragGripIcon}
                                tabIndex="-1"
                            />
                        </div>,
                    )}
                    <SuiTooltipPopover content={DEFAULT_BUTTON_TIP} openDelayed>
                        <SuiButton
                            className={`${BASE_CLASS_NAME}__radio--button`}
                            variation="transparent"
                            size="small"
                            onClickCapture={this.handleClickSelected}
                            icon={
                                allowMultipleSelected ? (
                                    <SuiCheckbox checked={optionSelected} />
                                ) : (
                                    <SuiRadio value={optionValue} checked={optionSelected} />
                                )
                            }
                        />
                    </SuiTooltipPopover>
                </SuiContainer>

                <SuiContainer className={`${BASE_CLASS_NAME}__label-container`} variation="flex-group">
                    {!isOpen && (
                        <SuiContainer variation="flex-vertical">
                            <TrimmedLabel>{optionLabel}</TrimmedLabel>

                            {optionValue !== optionLabel && (
                                <span className={`${BASE_CLASS_NAME}__subtitle`}>Value: {optionValue}</span>
                            )}

                            {Number.isFinite(responseLimit) && (
                                <p className={`${BASE_CLASS_NAME}__subtitle`}>
                                    {RESPONSE_LIMIT_HEADER_TEXT} {responseLimit}
                                </p>
                            )}
                        </SuiContainer>
                    )}
                </SuiContainer>

                <SuiContainer variation="flex-group">
                    {isOpen && <SuiButton variation="text" label={DONE_BUTTON_LABEL} />}
                </SuiContainer>

                <CSSTransition
                    in={isDeleteButtonVisible}
                    timeout={TRANSITION_TIMEOUT}
                    classNames={`${BASE_CLASS_NAME}__button-transition`}
                >
                    <SuiContainer
                        variation="flex-group flex-horizontal"
                        className={[
                            `${BASE_CLASS_NAME}__button-container`,
                            isDeleteButtonVisible ? `${BASE_CLASS_NAME}__button-container--active` : null,
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    >
                        <SuiButton
                            variation="navigation"
                            size="small"
                            icon={TrashIcon}
                            title={REMOVE_BUTTON_TITLE}
                            onClick={isDeleteButtonVisible ? this.handleClickRemove : null}
                        />
                    </SuiContainer>
                </CSSTransition>
            </SuiContainer>
        );
    }

    renderContent() {
        const { optionLabel, optionValue, showEditValueInputs, responseLimit, isAllowedResponseLimit, protectedField } =
            this.props;

        return (
            <SuiContainer variation="flex-vertical" className={`${BASE_CLASS_NAME}__options--config`}>
                <SuiContainer>
                    <SuiTextInput
                        label={showEditValueInputs ? OPTION_LABEL_LABEL : OPTION_LABEL_LABEL_SIMPLE}
                        placeholder={showEditValueInputs ? OPTION_LABEL_PLACEHOLDER : OPTION_LABEL_PLACEHOLDER_SIMPLE}
                        value={optionLabel}
                        onChange={this.handleEditLabel}
                        onBlur={this.handleTrimLabel}
                        focusOnMount
                        fullWidth
                    />
                </SuiContainer>
                <SuiContainer variation="flex-horizontal" className={`${BASE_CLASS_NAME}__options--extras`}>
                    {cond([
                        [
                            () => this.props.isAllowedLogicNode && isAllowedResponseLimit && protectedField,
                            () => (
                                <SuiTooltipPopover align="left" content={DISABLED_OTHER} disabled>
                                    <SuiContainer className={`${BASE_CLASS_NAME}__options--response-limit`}>
                                        <SuiTextInput
                                            label={RESPONSE_LIMIT_LABEL}
                                            type="number"
                                            disabled
                                            placeholder={RESPONSE_LIMIT_PLACEHOLDER}
                                            fullWidth
                                        />
                                    </SuiContainer>
                                </SuiTooltipPopover>
                            ),
                        ],
                        [
                            () => this.props.isAllowedLogicNode && isAllowedResponseLimit,
                            () => (
                                <SuiContainer className={`${BASE_CLASS_NAME}__options--response-limit`}>
                                    <SuiTextInput
                                        value={responseLimit != null ? responseLimit : ''}
                                        label={
                                            <SuiContainer variation="flex-horizontal">
                                                <ResponseLimitContainer>{RESPONSE_LIMIT_LABEL}</ResponseLimitContainer>
                                                <SuiTooltip>
                                                    {this.props.responseLimitFlag
                                                        ? OPTION_TOOLTIP_LABEL
                                                        : OPTION_TOOLTIP_LABEL_FREEMIUM}
                                                </SuiTooltip>
                                            </SuiContainer>
                                        }
                                        type="number"
                                        min={0}
                                        disabled={!this.props.responseLimitFlag}
                                        placeholder={RESPONSE_LIMIT_PLACEHOLDER}
                                        fullWidth
                                        onChange={this.handleEditResponseLimit}
                                    />
                                </SuiContainer>
                            ),
                        ],
                        [() => true, () => null],
                    ])()}
                    {showEditValueInputs && (
                        <SuiContainer>
                            <SuiTextInput
                                label={OPTION_VALUE_LABEL}
                                value={optionValue}
                                placeholder={OPTION_VALUE_PLACEHOLDER}
                                onChange={this.handleEditValue}
                                fullWidth
                            />
                        </SuiContainer>
                    )}
                </SuiContainer>
            </SuiContainer>
        );
    }

    render() {
        const { isOpen, isDropTargetActive, dropTargetYPosition, disabled } = this.props;

        return (
            <SuiContainer
                className={[
                    BASE_CLASS_NAME,
                    isDropTargetActive &&
                        (dropTargetYPosition < 0.5
                            ? `${BASE_CLASS_NAME}--drop-target-top`
                            : `${BASE_CLASS_NAME}--drop-target-bottom`),
                    isOpen && `${BASE_CLASS_NAME}--open`,
                    disabled && `${BASE_CLASS_NAME}--disabled`,
                ]
                    .filter(Boolean)
                    .join(' ')}
                onMouseOver={this.handleMouseOver}
                onMouseLeave={this.handleMouseLeave}
            >
                <SuiCollapsible
                    variation={`${CLEAN_BOX} ${NO_PADDED}`}
                    header={this.renderHeader()}
                    content={this.renderContent()}
                    icon={null}
                    isOpen={isOpen}
                    onToggleHeader={this.handleCollapsibleToggleHeader}
                />
            </SuiContainer>
        );
    }
}

const mapStateToProps = (state) => {
    const editNode = getEditFormNode(state);
    const formId = getFormId(state);
    const isAllowedResponseLimit = !isFormTypeTicketSale(state, formId);

    return {
        isAllowedLogicNode: isAllowedLogicNode(state, editNode),
        editNode,
        responseLimitFlag: getResponseLimitFlag(state),
        isAllowedResponseLimit,
        protectedField: editNode.protected,
    };
};

export default connect(mapStateToProps)(FormEditorEditQuestionOption);
